import type { TenantConfig, TenantConfigs } from "../types/tenant";
// Committed fallback, generated from the DB by the tenant export/fetch scripts.
// At runtime the API overrides this with live DB rows (see setActiveTenantConfigs);
// this file is only the seed/offline fallback for local dev, tests, and the
// frontend build before `fetch-configs` runs.
import tenantConfigsJson from "../generated/tenant-configs.json";

/**
 * Active tenant config registry.
 *
 * Mutated in place by `setActiveTenantConfigs` so every consumer that reads this
 * array live (the per-request resolvers below) sees DB-sourced configs after the
 * API hydrates them, while keeping the same array reference (no `export let`
 * live-binding fragility). Seeded from the committed fallback JSON.
 */
export const TENANT_CONFIGS: TenantConfigs =
  (tenantConfigsJson as TenantConfigs) ?? [];

/**
 * Immutable snapshot of the committed seed, captured at module load before any
 * `setActiveTenantConfigs` mutation splices `TENANT_CONFIGS`. Used as a
 * per-field fallback when a DB row omits a section the committed config still
 * carries (e.g. `sports`), so a committed-only field can't silently vanish once
 * the DB drives the runtime registry.
 */
export const GENERATED_TENANT_CONFIGS: TenantConfigs = (
  (tenantConfigsJson as TenantConfigs) ?? []
).map((config) => ({ ...config }));

/** Where the currently-active configs came from. Observability for the API. */
export type TenantConfigSource = "fallback" | "database";
let tenantConfigSource: TenantConfigSource = "fallback";

export const getTenantConfigSource = (): TenantConfigSource => tenantConfigSource;

/**
 * Replace the active tenant config registry with DB-sourced rows.
 *
 * Called by the API at startup and on a refresh interval so the backoffice/DB is
 * the single source of truth at runtime. Mutates `TENANT_CONFIGS` in place to
 * preserve the shared array reference. A blank list is ignored so a transient
 * empty DB read can never wipe out the working set — the previous configs (DB or
 * committed fallback) stay in effect. Returns the active config count.
 */
export const setActiveTenantConfigs = (
  configs: TenantConfigs,
  source: TenantConfigSource = "database"
): number => {
  if (!Array.isArray(configs) || configs.length === 0) {
    return TENANT_CONFIGS.length;
  }
  TENANT_CONFIGS.splice(0, TENANT_CONFIGS.length, ...configs);
  tenantConfigSource = source;
  return TENANT_CONFIGS.length;
};

// Explicit, order-independent default. Resolving via TENANT_CONFIGS[0] made the
// system default depend on generated JSON sort order (alphabetical by
// clientSlug), which silently changed the default tenant. Pin it explicitly.
const DEFAULT_TENANT_ID = "byu";

const ensureConfigs = () => {
  if (!TENANT_CONFIGS.length) {
    throw new Error(
      "No tenant configs loaded. Run the tenant export script to generate packages/shared/src/generated/tenant-configs.json."
    );
  }
};

export const listTenantConfigs = (): TenantConfigs => {
  ensureConfigs();
  return TENANT_CONFIGS;
};

export const getTenantConfigById = (id: string): TenantConfig | undefined => {
  ensureConfigs();
  return TENANT_CONFIGS.find((config) => config.id === id);
};

export const getTenantConfigBySlug = (slug: string): TenantConfig | undefined => {
  ensureConfigs();
  return TENANT_CONFIGS.find((config) => config.clientSlug === slug);
};

const normalizeHost = (host?: string | null): string | undefined => {
  if (!host) return undefined;
  return host.split(":")[0]?.toLowerCase();
};

const matchesCloudflarePagesHost = (config: TenantConfig, host: string): boolean => {
  const project = config.deployment?.cloudflareProject?.trim().toLowerCase();
  if (!project) return false;
  return host === `${project}.pages.dev` || host.endsWith(`.${project}.pages.dev`);
};

const isPrivateIPv4 = (host: string): boolean => {
  if (host === "localhost" || host === "127.0.0.1") return true;
  const parts = host.split(".").map((p) => Number(p));
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return false;
  const [a, b] = parts;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
};

export const resolveTenantConfigByHostFromConfigs = (
  host: string | null | undefined,
  configs: TenantConfigs
): TenantConfig | undefined => {
  if (!configs.length) {
    return undefined;
  }

  const normalizedHost = normalizeHost(host);
  if (!normalizedHost) {
    return undefined;
  }

  const match = configs.find((config) =>
    config.domains.some((domain) => domain.toLowerCase() === normalizedHost)
  );
  if (match) {
    return match;
  }

  return configs.find((config) =>
    matchesCloudflarePagesHost(config, normalizedHost)
  );
};

export const resolveTenantConfigByHost = (
  host?: string | null
): TenantConfig => {
  ensureConfigs();
  const normalizedHost = normalizeHost(host);
  if (normalizedHost) {
    const match = resolveTenantConfigByHostFromConfigs(normalizedHost, TENANT_CONFIGS);
    if (match) {
      return match;
    }

    if (isPrivateIPv4(normalizedHost)) {
      const localhostMapped = TENANT_CONFIGS.find((config) =>
        config.domains.some((domain) => domain.toLowerCase() === "localhost")
      );
      if (localhostMapped) {
        return localhostMapped;
      }
    }
  }

  return getTenantConfigById(DEFAULT_TENANT_ID) ?? TENANT_CONFIGS[0];
};

export const getDefaultTenantConfig = (): TenantConfig =>
  getTenantConfigById(DEFAULT_TENANT_ID) ?? TENANT_CONFIGS[0];
